import { useEffect, useState } from 'react'
import { SciVizPageType, RestrictionStore, TabItem } from './SciVizInterfaces'
import SciVizGrid from './SciVizGrid'
import './Page.css'

/** The interface for the SciVizPage props */
interface PageProps {
    /** The data of the page */
    page: SciVizPageType

    /** A JWT token to perform queries */
    jwtToken?: string

    /** The authentication database for OIDC */
    databaseHost?: string

    /** A callback function for handling hidden pages */
    updateHiddenPage?: (
        route: string,
        queryParams: string,
        currPageMap: {
            [key: string]: TabItem
        }
    ) => void
}

/**
 * Dynamically creates a SciViz page
 *
 * @param {SciVizSpec} spec - The top level SciViz spec
 * @param {SciVizPageType} page - The data of the page
 * @param {string=} [jwtToken] - A JWT token to perform queries
 * @param {string=} databaseHost - The authentication database for OIDC
 * @param {(route: string, queryParams: string, currPageMap: {[key: string]: TabItem}) => void=} [updateHiddenPage] - A callback function for handling hidden pages
 *
 * @returns A SciViz page
 */
function SciVizPage(props: PageProps) {
    const [restrictionList, setRestrictionList] = useState<string[]>([])
    const [store, setStore] = useState<RestrictionStore>({})
    const pageData = props.page

    useEffect(() => {
        setRestrictionList(new URLSearchParams(window.location.search).toString().split('&'))
    }, [])

    /**
     * A callback function to refresh the restriction list
     * @param {string} queryParam - The query paramters to set the restriciton list as
     * @returns The query params
     */
    const updateRestrictionList = (queryParams: string): string => {
        setRestrictionList(new URLSearchParams(queryParams).toString().split('&'))
        return queryParams
    }

    /**
     * A callback function to refresh the page store
     * @param {string} key - The key of the component that the store object belongs to
     * @param {string[]} record - The list of key-values as strings to add to the store
     */
    const updateStore = (key: string, record: string[]) => {
        setStore((prevStore) => ({
            ...prevStore,
            [key]: record
        }))
    }

    return (
        <div>
            <div className='grid-container'>
                <ul className='grid-list'>
                    {Object.entries(pageData.grids).map(([name, grid]) => (
                        <SciVizGrid
                            key={JSON.stringify(grid)}
                            name={name}
                            databaseHost={props.databaseHost}
                            grid={grid}
                            jwtToken={props.jwtToken}
                            restrictionList={[...restrictionList]}
                            store={Object.assign({}, store)}
                            updateRestrictionList={updateRestrictionList}
                            updateStore={updateStore}
                            updateHiddenPage={props.updateHiddenPage}
                        />
                    ))}
                </ul>
            </div>
        </div>
    )
}

export default SciVizPage
